import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart } from "lucide-react";
import Reveal from "@/components/Reveal";

const LEAD = [
  "It all began in June 2018, when I became a mother for the first time. I began looking at food through an entirely different lens. Ingredients I had once barely noticed suddenly mattered. I found myself turning over packets in grocery stores, reading ingredient lists and looking up unfamiliar names. I wanted to understand exactly what was going into my daughter\u2019s body and make better choices for her as she grew.",
  "But the more labels I read, the more difficult it seemed to find everyday treats made with the kind of simple, wholesome ingredients I was looking for.",
  "And as my children grew, so did their love for something every child seems to adore \u2014 cake!",
  "I wanted them to enjoy those little treats and celebrations, but I also wanted to feel good about what went into them. So I started experimenting at home, replacing conventional ingredients with ones I would happily choose for my own family.",
  "Somewhere along the way, baking together became our Sunday ritual \u2014 a little family tradition filled with messy hands, warm ovens, laughter and, of course, plenty of cake. Those Sundays became some of our sweetest memories.",
];

const AFTER = [
  "That little thought eventually became Almo Bakes.",
  "We created our baking mixes around the same philosophy that started in my own kitchen: keep the ingredients simple, choose them thoughtfully, and never forget that wholesome food should still taste delicious.",
  "That\u2019s why our mixes are made with ingredients such as organic almond flour and organic coconut sugar, without the long, complicated ingredient lists that first made me start reading labels all those years ago.",
  "Almo Bakes isn\u2019t just about making cake easier. It\u2019s about giving families a simple way to bake together, enjoy something delicious and create the little moments that children remember long after the last slice is gone.",
];

export default function OurStory() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="pt-[112px]">
      <div className="absolute top-40 -left-32 w-[420px] h-[420px] rounded-full bg-[#F8C8DC]/40 blur-[120px] pointer-events-none" />
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-24 relative" data-testid="our-story-page">
        <Reveal>
          <Link
            to="/"
            data-testid="story-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#6E5E57] hover:text-[#6A2C3E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C86D51] mt-12 mb-5">Our Story</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#2C221E] leading-[1.1]">
            From our family,<br />
            <em className="italic text-[#6A2C3E]">to yours</em>
          </h1>
        </Reveal>

        <div className="mt-12 space-y-7">
          {LEAD.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className={`text-lg leading-relaxed text-[#2C221E]/80 ${i === 0 ? "story-dropcap" : ""}`}>{p}</p>
            </Reveal>
          ))}
        </div>

        <Reveal className="my-14">
          <div className="rounded-[2rem] overflow-hidden spotlight-frame rotate-1 group">
            <img
              src="/assets/daughter-baking.png"
              alt="A two-year-old girl in an apron spooning cake batter and pouring cocoa into a mixing bowl"
              className="w-full aspect-[3/2] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
          </div>
          <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#6E5E57]">
            My daughter, baking at the age of two
          </p>
        </Reveal>

        <Reveal>
          <p className="text-lg leading-relaxed text-[#2C221E]/80">And they made me wonder:</p>
          <blockquote className="my-10 border-l-2 border-[#C86D51] pl-7 sm:pl-9">
            <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-[#6A2C3E] tracking-tight">
              What if baking with better ingredients could be this simple for every family?
            </p>
          </blockquote>
        </Reveal>

        <div className="space-y-7">
          {AFTER.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className="text-lg leading-relaxed text-[#2C221E]/80">{p}</p>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 rounded-[2rem] bg-[#F8C8DC]/50 border border-[#6A2C3E]/10 px-8 sm:px-12 py-12 text-center">
          <p className="font-serif text-2xl sm:text-3xl font-semibold text-[#2C221E] tracking-tight inline-flex flex-wrap items-center justify-center gap-2.5">
            From our family to yours, made with care.
            <Heart className="w-6 h-6 text-[#C86D51] fill-current" />
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/mixes"
              data-testid="story-cta-mixes"
              className="rounded-full bg-[#2C221E] text-[#FAF7F2] px-8 py-4 text-sm font-medium hover:bg-[#6A2C3E] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
            >
              Meet the Mixes
            </Link>
            <Link
              to="/#contact"
              data-testid="story-cta-launch"
              className="rounded-full border border-[#2C221E]/25 px-8 py-4 text-sm font-medium text-[#2C221E] hover:bg-[#2C221E] hover:text-[#FAF7F2] transition-all duration-300"
            >
              Get Sweet Updates
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
